import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import { Send } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

const API_BASE = `${window.location.protocol}//${window.location.hostname}`;
const CONTACT_DATA_URL = `${API_BASE}/api/fos/contactData`;
const CONTACT_SUBMIT_URL = `${API_BASE}/contact-submit-2`;

function formatTime(timeStr) {
  if (!timeStr || timeStr.toLowerCase() === "closed") return "Closed";
  try {
    const [h, m] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return timeStr;
  }
}

const ContactUs = () => {
  const [loadingPage, setLoadingPage] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [data, setData] = useState(null);
const { settings } = useSettings();

  const recaptchaSiteKey = settings?.recaptcha_site_key;
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const recaptchaRef = useRef(null);

  // Fetch page data
  useEffect(() => {
    const fetchContactData = async () => {
      try {
        const res = await axios.get(CONTACT_DATA_URL);

        if (res.data.status === "success") {
          const list = res.data.data || [];
          const base = res.data.image_base_url || "";

          const obj = {};
          list.forEach((item) => {
            obj[item.name] = item.value;
          });

          // APPLY BG STATUS
          obj.bg_status = res.data.bg_status;

          // FULL IMAGE URL
          obj.fullBackgroundImage = obj.background_image
            ? base + obj.background_image
            : "";

          setData(obj);
        } else {
          setApiError("Failed to load contact data.");
        }
      } catch (error) {
        console.error(error);
        setApiError("Error loading contact data.");
      } finally {
        setLoadingPage(false);
      }
    };

    fetchContactData();
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const recaptchaToken = recaptchaRef.current?.getValue();
    if (!recaptchaToken) {
      toast.error("Please verify the reCAPTCHA.");
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("subject", form.subject);
      formData.append("message", form.message);
      formData.append("g-recaptcha-response", recaptchaToken);

      const res = await axios.post(CONTACT_SUBMIT_URL, formData);

      if (res.data === 1 || res.data === "1") {
        toast.success("Thank you for contacting us. We will get back to you soon.");
        setForm({ name: "", email: "", subject: "", message: "" });
        recaptchaRef.current?.reset();
      } else if (res.data === 0 || res.data === "0") {
        toast.error("Sorry, you cannot submit links.");
      } else {
        toast.error("Unexpected server response.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Loading contact page...</p>
      </div>
    );
  }

  if (apiError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-red-600">{apiError || "Unable to load contact page."}</p>
      </div>
    );
  }

  return (
    <section
      className="min-h-screen pb-16 bg-cover bg-center relative"
      style={{
        ...(data.bg_status == 1
          ? {
              backgroundImage: `url(${data.fullBackgroundImage})`,
              backgroundColor: "transparent",
            }
          : {
              backgroundColor: data.background_color,
              backgroundImage: "none",
            }),
      }}
    >
      {/* Overlay only if using image */}
      {data.bg_status == 1 && data.fullBackgroundImage && (
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
      )}

      {/* TITLE */}
      <div className="relative z-10 container mx-auto px-4 pt-10">
        <h2
          className="text-[42px] font-bold text-center"
          style={{ color: data.heading_color }}
        >
          {data.heading}
        </h2>
      </div>

      {/* MAP + FORM */}
      <div className="relative z-10 container mx-auto px-4 mt-10 grid lg:grid-cols-2 gap-8">
        
        {/* MAP */}
        <div className="h-full">
          <iframe
            title="map"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(
              data.address || "Australia"
            )}&output=embed&z=13`}
            className="w-full h-full min-h-[400px] rounded-xl border border-gray-200"
            allowFullScreen
            loading="lazy"
          ></iframe>
        </div>

        {/* FORM */}
        <div className="bg-white rounded-2xl shadow p-6 md:p-8">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">
            Send us a message
          </h3>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                name="name"
                placeholder="Name"
                required
                value={form.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500 outline-none"
              />

              <input
                type="email"
                name="email"
                placeholder="Email Address"
                required
                value={form.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500 outline-none"
              />
            </div>

            <input
              type="text"
              name="subject"
              placeholder="Subject"
              required
              value={form.subject}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500 outline-none"
            />

            <textarea
              name="message"
              placeholder="Type your message"
              rows={6}
              required
              value={form.message}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500 outline-none"
            />
          {recaptchaSiteKey && (
              <div className="mt-2">
                <ReCAPTCHA sitekey={recaptchaSiteKey} ref={recaptchaRef} />
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-4 w-full bg-emerald-600 text-white font-semibold py-2.5 rounded-md flex items-center justify-center gap-2 hover:bg-emerald-700 transition disabled:opacity-50"
            >
              <Send size={18} />
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
